import Typography from '@material-ui/core/Typography';
import { FC, ReactElement, useCallback, useMemo } from 'react';
import { useFormContext } from 'react-hook-form';

import layoutStyles from '../../../shared/components/EditAllocationFormLayout/EditAllocationFormLayout.module.scss';
import useAllocationChangeTracking from '../../../shared/hooks/useAllocationChangeTracking';
import { IEditAllocationFormValues } from '../../../shared/model/types';
import useLocationGroupAllocationsUpdate from '../../hooks/updateAllocations/useLocationGroupAllocationsUpdate';
import AllocationLocationGroupsSelect from '../SelectFields/AllocationLocationGroupsSelect';
import AllocationLocationGroupRowItem from '../ClientsRowItem/AllocationLocationGroupRowItem';
import { useEditClientAllocationContext } from '../../context/EditClientAllocationContext';

interface IProps {
  teamMemberIdx: number;
}

const AllocationLocationGroupsList: FC<IProps> = ({ teamMemberIdx }): ReactElement => {
  const { locationGroups } = useEditClientAllocationContext();
  const { markChanged, markDeleted } = useAllocationChangeTracking<IEditAllocationFormValues>({
    changedField: 'changedLocationGroupAllocationsIds',
    deletedField: 'deletedLocationGroupAllocationsIds',
  });
  const { fields, updateLocationGroupAllocations, removeLocationGroupAt } =
    useLocationGroupAllocationsUpdate(teamMemberIdx);
  const { getValues, setValue, watch } = useFormContext<IEditAllocationFormValues>();

  const locationGroupAllocations = watch(
    `teamMembersAllocations.${teamMemberIdx}.locationGroupAllocations` as const,
  );

  const activeClientIds = useMemo(
    () =>
      locationGroupAllocations.flatMap(lgAlloc => {
        const removed = lgAlloc.removedClientIds ?? [];
        return (
          locationGroups
            .find(lg => lg.locationGroup.id === lgAlloc.locationGroupId)
            ?.clients.filter(c => !removed.includes(c.id))
            .map(c => c.id) ?? []
        );
      }),
    [locationGroupAllocations, locationGroups],
  );

  const handleDeleteGroup = useCallback(
    (groupId: number) => {
      const fieldIdx = locationGroupAllocations.findIndex(f => f.locationGroupId === groupId);
      if (fieldIdx === -1) return;

      const field = locationGroupAllocations[fieldIdx];
      removeLocationGroupAt(fieldIdx);

      if (field.allocation?.id) {
        const isLastClientInLocationGroup = locationGroupAllocations.length < 2;

        if (isLastClientInLocationGroup) {
          markDeleted(field.allocation.id);
          return;
        }
        markChanged(field.allocation.id);
      }
    },
    [locationGroupAllocations, markChanged, markDeleted, removeLocationGroupAt],
  );

  const handleDeleteClient = useCallback(
    (groupId: number, clientId: number) => {
      const fieldIdx = locationGroupAllocations.findIndex(f => f.locationGroupId === groupId);
      if (fieldIdx === -1) return;

      const field = locationGroupAllocations[fieldIdx];
      const removedPath =
        `teamMembersAllocations.${teamMemberIdx}.locationGroupAllocations.${fieldIdx}.removedClientIds` as const;
      const currentRemoved = getValues(removedPath) ?? [];
      const nextRemoved = [...currentRemoved, clientId];

      const groupTotalClients =
        locationGroups.find(lg => lg.locationGroup.id === groupId)?.clients.length ?? 0;

      if (groupTotalClients - nextRemoved.length <= 0) {
        handleDeleteGroup(groupId);
      } else {
        setValue(removedPath, nextRemoved, { shouldDirty: true });
        if (field.allocation?.id) markChanged(field.allocation.id);
      }
    },
    [
      getValues,
      handleDeleteGroup,
      locationGroupAllocations,
      locationGroups,
      markChanged,
      setValue,
      teamMemberIdx,
    ],
  );

  return (
    <>
      <Typography variant="subtitle1" className={layoutStyles.assignedClientsSectionTitle}>
        Location Groups
      </Typography>

      <AllocationLocationGroupsSelect
        teamMemberIdx={teamMemberIdx}
        updateLocationGroupAllocations={updateLocationGroupAllocations}
        activeClientIds={activeClientIds}
        onDeleteClient={handleDeleteClient}
        onDeleteGroup={handleDeleteGroup}
      />

      {fields.map((field, fieldIdx) => (
        <AllocationLocationGroupRowItem
          key={field.id}
          field={field}
          fieldIdx={fieldIdx}
          teamMemberIdx={teamMemberIdx}
          onRemoveClient={handleDeleteClient}
          onMarkChanged={markChanged}
        />
      ))}
    </>
  );
};

export default AllocationLocationGroupsList;
